import type { AnonymisationType } from './pupils';

// ---- Types ------------------------------------------------------------------

export interface ImportContext {
  scope: 'school' | 'individual';
  schoolId: string | null;
  userId: string;
  availableClasses: {
    id: string;
    name: string;
    yearGroupId: string | null;
    yearGroupName: string;
  }[];
  availableYearGroups: { id: string; name: string }[];
  /** All accessible pupils, used for duplicate detection */
  existingPupils: { first_name: string; last_name: string; class_id: string }[];
}

export type RowStatus = 'valid' | 'error' | 'duplicate';

export interface ParsedRow {
  rowNumber: number;
  first_name: string;
  last_name: string;
  year_group: string;
  class_name: string;
  anonymisation: AnonymisationType;
  is_archived: boolean;
  status: RowStatus;
  errors: string[];
  resolvedClassId?: string;
  resolvedYearGroupId?: string;
  /** True if same first+last+year+class appears more than once in the CSV */
  isDuplicateInCsv: boolean;
  /** True if a matching first+last in the same resolved class already exists in the DB */
  isDuplicateInDb: boolean;
  /** Individual plan only — class doesn't exist yet and will be created during import */
  willCreateClass?: boolean;
  /** Set to true after a successful DB insert */
  imported?: boolean;
}

export interface ImportSummary {
  total: number;
  imported: number;
  skipped: number;
  failed: number;
}

// ---- CSV parsing ------------------------------------------------------------

const REQUIRED_COLUMNS = ['first_name', 'last_name', 'year_group', 'class_name'] as const;

/** Minimal RFC-4180-compatible CSV line splitter (handles quoted fields). */
function splitCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      // Escaped quote inside quoted field
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

interface RawCsvRow {
  rowNumber: number;
  first_name: string;
  last_name: string;
  year_group: string;
  class_name: string;
  anonymisation: string;
  archived: string;
}

export interface ParseCsvResult {
  rows: RawCsvRow[];
  parseError?: string;
}

export function parseCsv(text: string): ParseCsvResult {
  const lines = text.split(/\r?\n/);

  // Find the first non-blank line as the header
  let headerLineIndex = 0;
  while (headerLineIndex < lines.length && lines[headerLineIndex].trim() === '') {
    headerLineIndex++;
  }
  if (headerLineIndex >= lines.length) {
    return { rows: [], parseError: 'File is empty.' };
  }

  const headers = splitCsvLine(lines[headerLineIndex]).map((h) =>
    h.toLowerCase().replace(/\s+/g, '_'),
  );

  const missing = REQUIRED_COLUMNS.filter((c) => !headers.includes(c));
  if (missing.length > 0) {
    return {
      rows: [],
      parseError: `Missing required columns: ${missing.join(', ')}. Found: ${headers.join(', ')}`,
    };
  }

  const get = (values: string[], col: string): string => {
    const idx = headers.indexOf(col);
    return idx >= 0 ? (values[idx] ?? '') : '';
  };

  const rows: RawCsvRow[] = [];
  let rowNumber = 0;

  for (let i = headerLineIndex + 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') continue;

    const values = splitCsvLine(line);
    if (values.every((v) => v === '')) continue; // fully empty row

    rowNumber++;
    rows.push({
      rowNumber,
      first_name: get(values, 'first_name'),
      last_name: get(values, 'last_name'),
      year_group: get(values, 'year_group'),
      class_name: get(values, 'class_name'),
      anonymisation: get(values, 'anonymisation'),
      archived: get(values, 'archived'),
    });
  }

  return { rows };
}

// ---- Validation & resolution ------------------------------------------------

const VALID_ANONYMISATION: AnonymisationType[] = ['full_name', 'first_initial', 'initials_only'];

function normaliseBoolean(val: string): boolean | null {
  const v = val.toLowerCase().trim();
  if (v === 'true' || v === 'yes' || v === '1') return true;
  if (v === 'false' || v === 'no' || v === '0') return false;
  return null;
}

/**
 * Validate and resolve each raw CSV row against the import context.
 * Pure function — does not touch the database.
 */
export function validateAndResolveRows(
  raw: RawCsvRow[],
  context: ImportContext,
): ParsedRow[] {
  // Lookup maps built once up-front
  const ygByName = new Map<string, string>(); // lc name → id
  for (const yg of context.availableYearGroups) {
    ygByName.set(yg.name.toLowerCase(), yg.id);
  }

  const classByYgAndName = new Map<string, string>(); // "ygId::lc name" → classId
  for (const cls of context.availableClasses) {
    if (cls.yearGroupId) {
      classByYgAndName.set(`${cls.yearGroupId}::${cls.name.toLowerCase()}`, cls.id);
    }
  }

  const existingKeys = new Set<string>(); // "lc first::lc last::classId"
  for (const p of context.existingPupils) {
    existingKeys.add(
      `${p.first_name.toLowerCase()}::${p.last_name.toLowerCase()}::${p.class_id}`,
    );
  }

  // Count intra-CSV duplicates: key = "lc first::lc last::lc yg::lc class"
  const csvKeyCount = new Map<string, number>();
  for (const row of raw) {
    const key = `${row.first_name.toLowerCase()}::${row.last_name.toLowerCase()}::${row.year_group.toLowerCase()}::${row.class_name.toLowerCase()}`;
    csvKeyCount.set(key, (csvKeyCount.get(key) ?? 0) + 1);
  }

  return raw.map((row) => {
    const errors: string[] = [];
    let anonymisation: AnonymisationType = 'full_name';
    let is_archived = false;
    let resolvedYearGroupId: string | undefined;
    let resolvedClassId: string | undefined;
    let willCreateClass: boolean | undefined;

    // --- Required field presence ---
    if (!row.first_name) errors.push('first_name is required');
    if (!row.last_name) errors.push('last_name is required');
    if (!row.year_group) errors.push('year_group is required');
    if (!row.class_name) errors.push('class_name is required');

    // --- Optional: anonymisation ---
    if (row.anonymisation) {
      if (VALID_ANONYMISATION.includes(row.anonymisation as AnonymisationType)) {
        anonymisation = row.anonymisation as AnonymisationType;
      } else {
        errors.push(
          `anonymisation "${row.anonymisation}" is invalid. Must be one of: ${VALID_ANONYMISATION.join(', ')}`,
        );
      }
    }

    // --- Optional: archived ---
    if (row.archived) {
      const bool = normaliseBoolean(row.archived);
      if (bool === null) {
        errors.push('archived must be true/false, yes/no, or 1/0');
      } else {
        is_archived = bool;
      }
    }

    // --- Resolve year_group ---
    if (row.year_group) {
      const ygId = ygByName.get(row.year_group.toLowerCase());
      if (ygId) {
        resolvedYearGroupId = ygId;
      } else {
        errors.push(
          `year_group "${row.year_group}" not found in school structure`,
        );
      }
    }

    // --- Resolve class ---
    if (resolvedYearGroupId && row.class_name) {
      const classKey = `${resolvedYearGroupId}::${row.class_name.trim().toLowerCase()}`;
      const classId = classByYgAndName.get(classKey);
      if (classId) {
        resolvedClassId = classId;
      } else if (context.scope === 'individual') {
        // Individual plan: class will be created during import — mark as willCreateClass
        // resolvedClassId stays undefined; resolvedYearGroupId is set
        willCreateClass = true;
      } else {
        // School plan: class must already exist
        const ygName = context.availableYearGroups.find((y) => y.id === resolvedYearGroupId)?.name ?? row.year_group;
        errors.push(
          `Class "${row.class_name}" not found in ${ygName}. Add it in School Setup → Classes first.`,
        );
      }
    }

    // --- Duplicate detection ---
    const csvKey = `${row.first_name.toLowerCase()}::${row.last_name.toLowerCase()}::${row.year_group.toLowerCase()}::${row.class_name.toLowerCase()}`;
    const isDuplicateInCsv = (csvKeyCount.get(csvKey) ?? 0) > 1;

    let isDuplicateInDb = false;
    if (resolvedClassId) {
      const dbKey = `${row.first_name.toLowerCase()}::${row.last_name.toLowerCase()}::${resolvedClassId}`;
      isDuplicateInDb = existingKeys.has(dbKey);
    }

    let status: RowStatus;
    if (errors.length > 0) {
      status = 'error';
    } else if (isDuplicateInCsv || isDuplicateInDb) {
      status = 'duplicate';
    } else {
      status = 'valid';
    }

    return {
      rowNumber: row.rowNumber,
      first_name: row.first_name,
      last_name: row.last_name,
      year_group: row.year_group,
      class_name: row.class_name,
      anonymisation,
      is_archived,
      status,
      errors,
      resolvedClassId,
      resolvedYearGroupId,
      isDuplicateInCsv,
      isDuplicateInDb,
      willCreateClass,
    };
  });
}
